
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Search, UserCircle } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";

interface ExerciseSearchFiltersProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  bodyPart: string;
  equipment: string;
  targetMuscle: string;
  setEquipment: (value: string) => void;
  setTargetMuscle: (value: string) => void;
  uniqueBodyParts: string[];
  uniqueEquipment: string[];
  uniqueTargetMuscles: string[];
  handleBodyPartChange: (value: string) => void;
  showCustomOnly?: boolean;
  onShowCustomChange?: (checked: boolean) => void;
}

export function ExerciseSearchFilters({
  searchTerm,
  setSearchTerm,
  bodyPart,
  equipment,
  targetMuscle,
  setEquipment,
  setTargetMuscle,
  uniqueBodyParts,
  uniqueEquipment,
  uniqueTargetMuscles,
  handleBodyPartChange,
  showCustomOnly,
  onShowCustomChange
}: ExerciseSearchFiltersProps) {
  const { t } = useTranslations("dashboard-exercise-library");

  return (
    <div>
      <div className="space-y-3">
        <div className="relative flex-grow w-full">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground dark:text-gray-400" />
          <Input
            placeholder={t("search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 bg-white border border-slate-100 rounded-xl shadow-sm focus:ring-1 focus:ring-flame/20 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        
        {onShowCustomChange && (
          <div className="flex items-center gap-2 w-full mb-3">
            <Switch
              id="custom-only"
              checked={showCustomOnly}
              onCheckedChange={onShowCustomChange}
            />
            <label
              htmlFor="custom-only"
              className="text-sm text-muted-foreground dark:text-gray-300 whitespace-nowrap flex items-center gap-1"
            >
              <UserCircle className="h-4 w-4" />
              {t("showCustomOnly")}
            </label>
          </div>
        )}
      </div>
        
      <div className="grid grid-cols-3 gap-2 w-full mt-3">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground dark:text-gray-400">{t("bodyPart")}</div>
          <Select
            value={bodyPart}
            onValueChange={handleBodyPartChange}
          >
            <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full">
              <SelectValue placeholder={t("bodyPart")} />
            </SelectTrigger>
            <SelectContent className="bg-white z-50 dropdown-opaque dark:bg-gray-700 dark:border-gray-600">
              <SelectItem value="_all" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("allBodyParts")}</SelectItem>
              {uniqueBodyParts.map(part => (
                <SelectItem key={part} value={part} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{part}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <div className="text-xs text-muted-foreground dark:text-gray-400">{t("equipment")}</div>
          <Select
            value={equipment}
            onValueChange={setEquipment}
          >
            <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full">
              <SelectValue placeholder={t("equipment")} />
            </SelectTrigger>
            <SelectContent className="bg-white z-50 dropdown-opaque dark:bg-gray-700 dark:border-gray-600">
              <SelectItem value="_all" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("allEquipment")}</SelectItem>
              {uniqueEquipment.map(equip => (
                <SelectItem key={equip} value={equip} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{equip}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <div className="text-xs text-muted-foreground dark:text-gray-400">{t("targetMuscle")}</div>
          <Select
            value={targetMuscle}
            onValueChange={setTargetMuscle}
          >
            <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full">
              <SelectValue placeholder={t("targetMuscle")} />
            </SelectTrigger>
            <SelectContent className="bg-white z-50 dropdown-opaque dark:bg-gray-700 dark:border-gray-600">
              <SelectItem value="_all" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("allMuscles")}</SelectItem>
              {uniqueTargetMuscles.map(muscle => (
                <SelectItem key={muscle} value={muscle} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{muscle}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
